/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  EditingState,
  IntegratedSorting,
  SortingState,
  DataTypeProvider,
  SearchState,
  IntegratedFiltering,
  PagingState,
  IntegratedPaging,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableEditRow,
  TableEditColumn,
  Toolbar,
  SearchPanel,
  DragDropProvider,
  TableColumnReordering,
  TableColumnResizing,
  TableColumnVisibility,
  ColumnChooser,
  PagingPanel,
} from '@devexpress/dx-react-grid-material-ui';
import { Command, PopupEditing, errorAlert } from '../../Shared';
import { SearchTable, AlertLocal } from '../../components';
import { getRowId, userroles } from '../../common';
import { getColumns } from '../../common/columns';
import {
  getStartWorks,
  getProducts,
  createStartWork,
  updateStartWork,
  deleteStartWork,
} from '../../graphql';
import { useLazyQuery, useMutation } from '@apollo/client';
import { timeFormatter, userFormatter } from '../../Shared/colorFormat';
import { IconButton, Typography } from '@material-ui/core';
import useEmployees from '../../hooks/useEmployees';
import { useUsers } from '../../hooks';
import { getTasks } from '../../graphql/query';
import { TableComponent } from '../components/TableComponent';
import PopupStartWork from '../../pubups/PopupStartWork';
import HRContext from '../../contexts/hr';
import { useReactToPrint } from 'react-to-print';
import MyIcon from '../components/MyIcon';
import { StartWorkPrint } from '../../print/StartWorkPrint';

const StartWork = forwardRef((props: any, ref) => {
  const { theme, isRTL, words, company, start, end } = props;
  const [alrt, setAlrt] = useState({ show: false, msg: '', type: undefined });

  const col = getColumns({ isRTL, words });

  const columns = [
    { name: 'time', title: col.time.title },
    { name: 'docNo', title: col.docNo.title },
    { name: col.employee.name, title: col.employee.title },
    { name: 'title', title: col.title.title },
    { name: 'desc', title: col.desc.title },
    { name: 'userId', title: col.userId.title },
    { name: 'print', title: ' ' },
  ];

  // Define refresQuery before mutations to avoid hoisting issues
  const refresQuery = {
    refetchQueries: [
      {
        query: getStartWorks,
        variables: {
          start: start ? start.setHours(0, 0, 0, 0) : undefined,
          end: end ? end.setHours(23, 59, 59, 999) : undefined,
        },
      },
      { query: getProducts },
      { query: getTasks },
    ],
  };

  // Define mutations at the very top to avoid hoisting issues
  const [addStartWork] = useMutation(createStartWork, refresQuery);
  const [editStartWork] = useMutation(updateStartWork, refresQuery);
  const [deleteStartWorkMutation] = useMutation(deleteStartWork, refresQuery);

  const commitChanges = async ({
    deleted,
    changed,
  }: {
    deleted?: any[];
    changed?: any;
  }) => {
    if (deleted) {
      const _id = deleted[0];
      const res = await deleteStartWorkMutation({ variables: { _id } });
      if (res?.data?.deleteStartWork?.ok === false) {
        await errorAlert(setAlrt, isRTL);
      } else {
        setRows(rows.filter((row: any) => row._id !== _id));
      }
    }
    if (changed) {
      const [_id, data]: [string, any] = Object.entries(changed)[0];
      const variables = {
        _id,
        ...data,
        contract: data.contractId
          ? {
              contractId: data.contractId,
              contractName: data.contractName,
              contractNameAr: data.contractNameAr,
            }
          : undefined,
        department: data.departmentId
          ? {
              departmentId: data.departmentId,
              departmentName: data.departmentName,
              departmentNameAr: data.departmentNameAr,
              departmentColor: data.departmentColor,
            }
          : undefined,
        employee: data.employeeId
          ? {
              employeeId: data.employeeId,
              employeeName: data.employeeName,
              employeeNameAr: data.employeeNameAr,
            }
          : undefined,
      };
      const res = await editStartWork({ variables });
      if (res?.data?.updateStartWork?.ok === false) {
        await errorAlert(setAlrt, isRTL);
      }
    }
  };

  const [tableColumnExtensions]: any = useState([
    { columnName: 'time', width: 120 },
    { columnName: 'docNo', width: 120 },
    { columnName: col.employee.name, width: 200 },
    { columnName: 'title', width: 150 },
    { columnName: 'desc', width: 200 },
    { columnName: 'userId', width: 150 },
    { columnName: 'print', width: 80, align: 'center' },
  ]);

  const [rows, setRows] = useState([]);
  const { employeesfull: employees } = useEmployees();
  const { users } = useUsers();
  const [printData, setPrintData] = useState<any>(null);

  const printComponentRef: any = useRef();
  const handleStartWorkPrint = useReactToPrint({
    content: () => printComponentRef.current,
    documentTitle: isRTL ? 'نموذج مباشرة عمل' : 'Start Work Form',
    removeAfterPrint: true,
  });

  const {
    state: { hiddenColumnNames },
    dispatch,
  } = useContext(HRContext);

  const setHiddenColumnNames = (hiddenColumns: any) => {
    dispatch({ type: 'setHiddenColumnNames', payload: hiddenColumns });
  };

  const [loadStartWorks, opData]: any = useLazyQuery(getStartWorks, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    const variables = {
      start: start ? start.setHours(0, 0, 0, 0) : undefined,
      end: end ? end.setHours(23, 59, 59, 999) : undefined,
    };
    // Always fetch data, with or without date filters
    loadStartWorks({
      variables,
    });
  }, [start, end]);

  // Initial fetch on component mount
  useEffect(() => {
    loadStartWorks({
      variables: {
        start: undefined,
        end: undefined,
      },
    });
  }, []);

  useImperativeHandle(ref, () => ({
    refresh() {
      if (opData?.refetch) {
        opData.refetch();
      }
    },
  }));

  useEffect(() => {
    if (opData?.data?.getStartWorks?.data) {
      const { data } = opData.data.getStartWorks;
      setRows(data);
    }
  }, [opData]);

  return (
    <>
      <Grid rows={rows} columns={columns} getRowId={getRowId}>
        <SortingState />
        <EditingState onCommitChanges={commitChanges} />
        <SearchState />
        <PagingState defaultCurrentPage={0} defaultPageSize={10} />
        <IntegratedSorting />
        <IntegratedFiltering />
        <IntegratedPaging />
        <DragDropProvider />
        <Table
          messages={{
            noData: isRTL ? 'لا يوجد بيانات' : 'no data',
          }}
          tableComponent={TableComponent}
          rowComponent={(props: any) => (
            <Table.Row {...props} style={{ height: 60 }}></Table.Row>
          )}
          columnExtensions={tableColumnExtensions}
        />
        <DataTypeProvider
          for={['time']}
          formatterComponent={timeFormatter}
        ></DataTypeProvider>
        <DataTypeProvider
          for={['userId']}
          formatterComponent={(props: any) =>
            userFormatter({ ...props, users })
          }
        ></DataTypeProvider>
        <DataTypeProvider
          for={['print']}
          formatterComponent={({ row }: any) => {
            return (
              <IconButton
                style={{ height: 40 }}
                onClick={() => {
                  const employeeProfile = (employees || []).find(
                    (emp: any) => emp._id === row?.employeeId,
                  );
                  setPrintData({
                    ...row,
                    employeeProfile,
                  });
                  setTimeout(() => {
                    handleStartWorkPrint();
                  }, 150);
                }}
              >
                <MyIcon size={20} icon="print"></MyIcon>
              </IconButton>
            );
          }}
        ></DataTypeProvider>
        <TableEditRow />
        <TableEditColumn
          showAddCommand={userroles?.sales?.add}
          showEditCommand
          showDeleteCommand
          commandComponent={Command}
        />
        <TableColumnReordering
          defaultOrder={[
            'time',
            'docNo',
            col.employee.name,
            'title',
            'desc',
            'userId',
            'print',
          ]}
        />
        <TableColumnResizing defaultColumnWidths={tableColumnExtensions} />

        <TableHeaderRow
          showSortingControls
          titleComponent={({ children }) => {
            return (
              <Typography style={{ fontSize: 14, fontWeight: 'bold' }}>
                {children}
              </Typography>
            );
          }}
        />
        <TableColumnVisibility
          defaultHiddenColumnNames={hiddenColumnNames}
          hiddenColumnNames={hiddenColumnNames}
          onHiddenColumnNamesChange={setHiddenColumnNames}
        />
        <Toolbar
          rootComponent={(props: any) => (
            <Toolbar.Root
              style={{
                position: 'absolute',
                left: isRTL ? 40 : undefined,
                right: isRTL ? undefined : 40,
                top: 62,
                zIndex: 115,
              }}
              {...props}
            ></Toolbar.Root>
          )}
        />
        <ColumnChooser />
        <PagingPanel pageSizes={[5, 10, 20, 50]} />

        <SearchPanel
          inputComponent={(props: any) => {
            return <SearchTable isRTL={isRTL} {...props}></SearchTable>;
          }}
        />
        <PopupEditing addAction={addStartWork} editAction={editStartWork}>
          <PopupStartWork
            employees={employees}
            company={company}
            theme={theme}
            isRTL={isRTL}
            words={words}
            onRefresh={() => {
              if (opData?.refetch) {
                opData.refetch();
              }
            }}
          ></PopupStartWork>
        </PopupEditing>
      </Grid>
      <div style={{ display: 'none' }}>
        <StartWorkPrint
          company={company}
          isRTL={isRTL}
          printData={printData}
          ref={printComponentRef}
        />
      </div>
      {alrt.show && (
        <AlertLocal
          isRTL={isRTL}
          message={alrt.msg}
          type={alrt.type}
          onClose={() => setAlrt({ show: false, msg: '', type: undefined })}
        />
      )}
    </>
  );
});

export default StartWork;
